import React, { useEffect, useState } from "react";
import { Button, Spin } from "antd";
import Image from "next/image";
import styles from "@aspen/theme/downBill.module.less";
import { downloadAttachmentFile, getAttachmentList } from "@aspen/services";
import type { IRes } from "@aspen/model";
import { message, WithEmptyContent } from "@aspen/ui";
import { downLoadFileBlob, getFileExtension, getFileMimeType, i18nUtil } from "@aspen/libs";
import { ModalFilePreview } from "./ModalFilePreview";
import noResultsImg from "@aspen/assets/images/no_results.png";

interface DocumentAttachmentTabProps {
  category: "fund_statement" | "financial_compliance";
}

interface IAttachmentItem {
  id: string;
  title: string;
  url: string;
}

const DocumentAttachmentTab: React.FC<DocumentAttachmentTabProps> = ({ category }) => {
  const intl = i18nUtil.t();

  const [loading, setLoading] = useState<boolean>(false);
  const [currentButtonIndex, setCurrentButtonIndex] = useState<number | null>();
  const [isDownload, setIsDownload] = useState<boolean>(false);
  const [documentList, setDocumentList] = useState<IAttachmentItem[]>([]);
  const [previewFile, setPreviewFile] = useState<IAttachmentItem | null>(null);

  // 获取附件列表
  const fetchAttachmentsList = () => {
    setLoading(true);
    getAttachmentList({ category })
      .then((res: IRes) => {
        if (res?.code === "0") {
          setDocumentList(res?.data || []);
        } else {
          message.error(intl?.[res?.msg] ?? res?.msg);
        }
      })
      .catch((err: any) => {
        message.error(intl?.[err?.msg] ?? err?.msg);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchAttachmentsList();
  }, [category]);

  // 处理下载
  const handleDownload = (item: IAttachmentItem, index: number) => {
    if (item.url) {
      setCurrentButtonIndex(index);
      setIsDownload(true);
      downloadAttachmentFile({ attachmentUrl: item.url, title: item.title })
        .then(res => {
          if (getFileExtension(item.url)) {
            const fileName = item.title + "." + getFileExtension(item.url);
            downLoadFileBlob(res, getFileMimeType(item.url), fileName);
          }
        })
        .finally(() => {
          setCurrentButtonIndex(null);
          setIsDownload(false);
        });
    }
  };

  const renderDocumentList = () => {
    return documentList.map((item, index) => (
      <div key={item.id} className={styles.billItem}>
        <div className={styles.flexRow}>
          <span className={styles.billName}>{item.title}</span>
        </div>
        {item?.url ? (
          <div className={`${styles.flexRow}`}>
            <Button className={styles.preview} onClick={() => setPreviewFile(item)}>
              <span>{intl["download.portfolio.report.preview"]}</span>
            </Button>
            <Button
              loading={currentButtonIndex === index ? isDownload : false}
              className={styles.download}
              onClick={() => handleDownload(item, index)}>
              <span> {intl["button.download"]}</span>
            </Button>
          </div>
        ) : null}
      </div>
    ));
  };

  return (
    <>
      <div className={styles.myInvestmentPlan} style={{ marginTop: 0 }}>
        <div className={styles.myInvestmentPlanArea}>
          <WithEmptyContent showEmpty>
            <Spin spinning={loading}>
              <div className={styles.wrapper}>
                {documentList.length ? (
                  renderDocumentList()
                ) : (
                  <div className={styles.img}>
                    <Image alt="" src={noResultsImg} width={240} height={180} />
                  </div>
                )}
              </div>
            </Spin>
          </WithEmptyContent>
        </div>
      </div>

      {/* 文件预览弹窗 */}
      {!!previewFile && (
        <ModalFilePreview
          previewFile={previewFile}
          onClose={() => {
            setPreviewFile(null);
          }}
        />
      )}
    </>
  );
};

export { DocumentAttachmentTab };
